import React from 'react';
import { Drawer } from '../Modals';
import Portal from '../Portal';
import { HiArrowLeft, HiMinus, HiSearch, HiX } from 'react-icons/hi';
import { FiAlertTriangle, FiCalendar, FiClock } from 'react-icons/fi';
import { dashboardStatusHexColors, getDashboardStatusLabel, getItemTypeColor } from '../../constants/integrationTypes';
import CustomTooltip from '../common/CustomTooltip';
import TaskHoverTooltip from '../common/TaskHoverTooltip';
import TaskCardTooltipContent from '../../pages/Resource/components/Timeline/TaskCardTooltipContent';
import StatusFilterWithColors from '../../pages/Resource/components/Timeline/StatusFilterWithColors';
import SortDropdown from '../../pages/Resource/components/Timeline/SortDropdown';
import { HighlightedText } from '../../utils/highlightUtils';
import { useResourceInfo } from '../../hooks/useResourceInfo';

interface Task {
	item_id: string;
	project_name: string;
	task_title: string;
	duration: number;
	start: string;
	end: string;
	project_color: string;
	project_id?: string;
	completion?: number;
	status?: string;
	integration_status?: string | null;
	due_date?: string | null;
	priority?: string | number | null;
	integration_item_type?: string;
	story_points?: string | number;
	story_points_computed?: boolean;
	completed_story_points?: string | number;
	completed_story_points_computed?: boolean;
	original_estimate?: string | number;
	original_estimate_computed?: boolean;
	completed_original_estimate?: string | number;
	completed_original_estimate_computed?: boolean;
	task_type?: string;
	stack_rank?: string | number | null;
	integration_team_name?: string;
	integration_project_name?: string;
	closed_outside_sprint?: boolean;
	responsible_id?: string;
}

interface AllTasksDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	tasks: Task[];
	selectedTaskId?: string;
	onSelectTask: (task: Task) => void;
	onDeleteTask: (task: Task) => void;
	searchText?: string;
	resourceName?: string;
	filterTitle?: string; // Custom title for filter context (e.g., "On Track Tasks", "User Story Tasks")
	initialStatusFilters?: string[];
	initialTypeFilter?: string;
	hideStatusFilter?: boolean;
	isSingleTeamSelected?: boolean;
	isSprintFilter?: boolean;
}

const statusPriorityMap: Record<string, number> = {
	delayed: 0,
	'not_started': 1,
	'on_track': 2,
	closed: 3
};

const getStatusPriority = (status?: string) => {
	if (!status) return Number.POSITIVE_INFINITY;
	const normalized = status.toLowerCase();
	return statusPriorityMap[normalized] ?? Number.POSITIVE_INFINITY;
};

const getStatusInfo = (task: Task) => {
	const statusKey = task.status?.toLowerCase?.() || '';
	const label = statusKey ? getDashboardStatusLabel(statusKey) : 'Not set';
	const color = (statusKey ? dashboardStatusHexColors[statusKey] : undefined) || '#a3a3a3';
	return { label, color };
};

const formatDate = (value?: string | null) => {
	if (!value) return 'Not set';
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return 'Not set';
	return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatIntegrationItemType = (type?: string) => {
	if (!type) return null;
	return type
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
};

const getCompletionValue = (task: Task) => {
	if (typeof task.completion === 'number') return task.completion;
	return 101;
};

const getPriorityValue = (priority?: string | number | null): number => {
	if (priority === null || priority === undefined || priority === '') return Number.MAX_SAFE_INTEGER;
	if (typeof priority === 'number') return priority;
	if (typeof priority === 'string') {
		const normalized = priority.toLowerCase().trim();
		if (normalized === 'high' || normalized === '1') return 1;
		if (normalized === 'medium' || normalized === '2') return 2;
		if (normalized === 'low' || normalized === '3') return 3;
		const numPriority = Number(priority);
		if (!Number.isNaN(numPriority)) return numPriority;
	}
	return Number.MAX_SAFE_INTEGER;
};

const getPriorityLabel = (priority?: string | number | null): string => {
	if (priority === null || priority === undefined || priority === '') return '';
	if (typeof priority === 'number') return `P${priority}`;
	if (typeof priority === 'string') {
		const normalized = priority.toLowerCase().trim();
		if (normalized === 'high' || normalized === '1') return 'P1';
		if (normalized === 'medium' || normalized === '2') return 'P2';
		if (normalized === 'low' || normalized === '3') return 'P3';
		return `P${priority}`;
	}
	return '';
};

const getPriorityStyles = (priority?: string | number | null): string => {
	if (priority === null || priority === undefined || priority === '') return '';
	const val = getPriorityValue(priority);
	if (val === 1) return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
	if (val === 2) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
	return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const getStackRankValue = (stackRank?: string | number | null): number => {
	if (stackRank === null || stackRank === undefined || stackRank === '') return Number.MAX_SAFE_INTEGER;
	if (typeof stackRank === 'number') return stackRank;
	if (typeof stackRank === 'string') {
		const numRank = Number.parseFloat(stackRank);
		if (!Number.isNaN(numRank)) return numRank;
	}
	return Number.MAX_SAFE_INTEGER;
};

const AllTasksDrawer: React.FC<AllTasksDrawerProps> = ({
	isOpen,
	onClose,
	tasks,
	selectedTaskId,
	onSelectTask,
	onDeleteTask,
	searchText = '',
	resourceName,
	filterTitle,
	initialStatusFilters = [],
	initialTypeFilter = '',
	hideStatusFilter = false,
	isSingleTeamSelected = false,
	isSprintFilter = false
}) => {
	const [localSearchText, setLocalSearchText] = React.useState('');
	const [statusFilters, setStatusFilters] = React.useState<string[]>(initialStatusFilters);
	const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
	const [sortBy, setSortBy] = React.useState<'priority' | 'progress' | 'alphabetical' | 'stack_rank'>(
		isSprintFilter ? 'stack_rank' : 'priority'
	);

	// Automatically group by team when multiple teams are selected in sprint mode
	const groupByTeam = isSprintFilter && !isSingleTeamSelected;

	// Get resource info for assigned avatar
	const { getResourceInfo } = useResourceInfo();

	// State for filtering by specific team/project group
	const [selectedGroupFilter, setSelectedGroupFilter] = React.useState<string>('all');
	const [groupFilterOpen, setGroupFilterOpen] = React.useState(false);

	// Sync statusFilters with initialStatusFilters whenever they change
	// This ensures clicking a new status filter properly updates the displayed tasks
	React.useEffect(() => {
		setStatusFilters(initialStatusFilters);
	}, [JSON.stringify(initialStatusFilters)]);

	const statusCounts = React.useMemo(() => {
		const counts: Record<string, number> = {};
		tasks.forEach(task => {
			const status = (task.status || '').toLowerCase();
			counts[status] = (counts[status] || 0) + 1;
		});
		return counts;
	}, [tasks]);

	// Update sort when stack rank availability changes
	const prevIsSprintFilterRef = React.useRef(isSprintFilter);

	React.useEffect(() => {
		const prevValue = prevIsSprintFilterRef.current;
		prevIsSprintFilterRef.current = isSprintFilter;

		if (prevValue !== isSprintFilter) {
			if (isSprintFilter) {
				setSortBy('stack_rank');
			} else {
				setSortBy('priority');
			}
		}
	}, [isSprintFilter]);

	const effectiveStatusFilters = statusFilters.length > 0 ? statusFilters : initialStatusFilters;

	const sortedTasks = React.useMemo(() => {
		// Calculate effective filters inside useMemo to avoid stale closure
		const effectiveFilters = statusFilters.length > 0 ? statusFilters : initialStatusFilters;

		let filtered = tasks.filter((task) => {
			if (localSearchText.trim()) {
				const searchLower = localSearchText.toLowerCase();
				const matchesSearch = (
					task.task_title?.toLowerCase().includes(searchLower) ||
					task.item_id?.toLowerCase().includes(searchLower) ||
					task.project_name?.toLowerCase().includes(searchLower)
				);
				if (!matchesSearch) return false;
			}

			if (effectiveFilters.length > 0) {
				const taskStatus = (task.status || '').toLowerCase().replace(/\s+/g, '_');
				const normalizedFilters = effectiveFilters.map(f => f.toLowerCase().replace(/\s+/g, '_'));
				if (!normalizedFilters.includes(taskStatus)) return false;
			}

			if (initialTypeFilter) {
				const taskType = (task.integration_item_type || task.task_type || '').toLowerCase();
				if (taskType !== initialTypeFilter.toLowerCase()) return false;
			}

			return true;
		});

		return filtered.sort((a, b) => {
			if (sortBy === 'priority') {
				const priorityA = getPriorityValue(a.priority);
				const priorityB = getPriorityValue(b.priority);
				const priorityDiff = priorityA - priorityB;
				if (priorityDiff !== 0) return priorityDiff;
			} else if (sortBy === 'progress') {
				const completionDiff = getCompletionValue(a) - getCompletionValue(b);
				if (completionDiff !== 0) return completionDiff;
			} else if (sortBy === 'alphabetical') {
				return a.task_title.localeCompare(b.task_title);
			} else if (sortBy === 'stack_rank') {
				const stackRankA = getStackRankValue(a.stack_rank);
				const stackRankB = getStackRankValue(b.stack_rank);
				const stackRankDiff = stackRankA - stackRankB;
				if (stackRankDiff !== 0) return stackRankDiff;
			}

			const statusDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
			if (statusDiff !== 0) return statusDiff;

			return a.task_title.localeCompare(b.task_title);
		});
	}, [tasks, localSearchText, statusFilters, initialStatusFilters, initialTypeFilter, sortBy, isSingleTeamSelected]);

	// Group tasks by team and project when groupByTeam is enabled
	const groupedTasks = React.useMemo(() => {
		if (!groupByTeam) return null;

		const groups: Record<string, Task[]> = {};

		sortedTasks.forEach(task => {
			// Create group key from both team and project name
			const teamName = task.integration_team_name || '';
			const projectName = task.integration_project_name || '';
			const groupKey = teamName && projectName
				? `${teamName} (${projectName})`
				: teamName || projectName || 'No Team/Project';

			if (!groups[groupKey]) {
				groups[groupKey] = [];
			}
			groups[groupKey].push(task);
		});

		// Sort tasks within each group based on selected sortBy
		Object.keys(groups).forEach(groupKey => {
			groups[groupKey].sort((a, b) => {
				if (sortBy === 'priority') {
					const priorityA = getPriorityValue(a.priority);
					const priorityB = getPriorityValue(b.priority);
					const priorityDiff = priorityA - priorityB;
					if (priorityDiff !== 0) return priorityDiff;
				} else if (sortBy === 'progress') {
					const completionDiff = getCompletionValue(a) - getCompletionValue(b);
					if (completionDiff !== 0) return completionDiff;
				} else if (sortBy === 'alphabetical') {
					return a.task_title.localeCompare(b.task_title);
				} else if (sortBy === 'stack_rank') {
					const stackRankA = getStackRankValue(a.stack_rank);
					const stackRankB = getStackRankValue(b.stack_rank);
					const stackRankDiff = stackRankA - stackRankB;
					if (stackRankDiff !== 0) return stackRankDiff;
				}
				// Secondary sort by status then title
				const statusDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
				if (statusDiff !== 0) return statusDiff;
				return a.task_title.localeCompare(b.task_title);
			});
		});

		// Sort group names alphabetically, but put 'No Team/Project' at the end
		const sortedGroupNames = Object.keys(groups).sort((a, b) => {
			if (a === 'No Team/Project') return 1;
			if (b === 'No Team/Project') return -1;
			return a.localeCompare(b);
		});

		return { groups, sortedGroupNames };
	}, [sortedTasks, groupByTeam, sortBy]);



	// Reusable task card renderer to avoid code duplication
	const renderTaskCard = (task: Task, index: number) => {
		const { label: statusLabel, color: statusColor } = getStatusInfo(task);
		const statusNormalized = (task.status || '').toLowerCase();
		const showRiskTriangle = statusNormalized === 'delayed' || statusNormalized === 'unallocated' || statusNormalized === 'blocked';
		const priorityLabel = getPriorityLabel(task.priority);
		const priorityStyles = getPriorityStyles(task.priority);

		return (
			<TaskHoverTooltip
				key={`${task.item_id}-${index}`}
				placement="left"
				arrow
				title={
					<TaskCardTooltipContent
						task={task as any}
						statusLabel={statusLabel}
						statusColor={statusColor}
						isSprintFilter={isSprintFilter}
						isSingleTeamSelected={isSingleTeamSelected}
					/>
				}
			>
				<div
					className="group relative bg-white dark:bg-slate-800/60 rounded-2xl border border-gray-100 dark:border-slate-700/50 hover:border-blue-200 dark:hover:border-blue-500/30 shadow-sm hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300 cursor-pointer overflow-hidden"
					onClick={() => onSelectTask(task)}
				>
					{/* Status indicator bar */}
					<div
						className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl"
						style={{ backgroundColor: statusColor }}
					/>

					<div className="pl-5 pr-4 py-4">
						{/* Top Row: Task Title, Avatar, Delete */}
						<div className="flex items-start gap-3">
							{/* Task Title */}
							<div className="flex-1 min-w-0 pr-2">
								<div className="flex items-center gap-1.5">
									{/* Risk Icon - next to title (delayed, blocked, or unallocated) */}
									{showRiskTriangle && (
										<CustomTooltip title="This work item is contributing to risk">
											<FiAlertTriangle className="flex-shrink-0 w-4 h-4 text-red-500" />
										</CustomTooltip>
									)}
									<h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2">
										<HighlightedText text={task.task_title} searchTerm={localSearchText || searchText} />
									</h4>
								</div>
							</div>

							{/* Delete Button */}
							<CustomTooltip title="Remove work item assignment">
								<button
									className="flex-shrink-0 rounded-full p-1 bg-red-600/95 text-white hover:bg-red-700 dark:hover:bg-red-500 transition-colors duration-200"
									aria-label="Remove work item assignment"
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
										e.stopPropagation();
										onDeleteTask(task);
									}}
								>
									<HiMinus className="w-3 h-3 text-white" />
								</button>
							</CustomTooltip>
						</div>

						{/* Middle Row: Badges */}
						<div className="mt-3 flex items-center gap-2 flex-wrap">
							{/* Status Badge */}
							<span
								className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold text-white"
								style={{ backgroundColor: statusColor }}
							>
								{statusLabel}{task.integration_status && task.integration_status.toLowerCase() !== (task.status || '').toLowerCase() && ` (${task.integration_status.charAt(0).toUpperCase() + task.integration_status.slice(1).toLowerCase()})`}
							</span>

							{/* Priority Badge */}
							{priorityLabel && (
								<span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${priorityStyles}`}>
									{priorityLabel}
								</span>
							)}

							{/* Stack Rank Badge - show when sprint filter is active */}
							{isSprintFilter && task.stack_rank !== null && task.stack_rank !== undefined && task.stack_rank !== '' && (
								<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
									SR: {task.stack_rank}
								</span>
							)}

							{/* Integration Type */}
							{task.integration_item_type && (
								<span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${getItemTypeColor(task.integration_item_type)}`}>
									{formatIntegrationItemType(task.integration_item_type)}
								</span>
							)}

							{/* Story Points */}
							{task.story_points && task.story_points !== '0' && ['user story', 'bug', 'spike'].includes(task.integration_item_type?.toLowerCase() || '') && (
								<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-semibold">
									<span>SP: {task.story_points}</span>
									{task.story_points_computed && (
										<CustomTooltip title="Story points computed by Cadence Pro">
											<span className="text-amber-500 cursor-help">★</span>
										</CustomTooltip>
									)}
									{task.completed_story_points !== undefined && task.completed_story_points !== null && (
										<>
											<span className="text-blue-400 dark:text-blue-500">|</span>
											<span className="text-green-600 dark:text-green-400">{task.completed_story_points}</span>
											{task.completed_story_points_computed && (
												<CustomTooltip title="Actual story points computed by Cadence Pro">
													<span className="text-amber-500 cursor-help">★</span>
												</CustomTooltip>
											)}
											<span className="text-blue-600 dark:text-blue-300 font-normal">Completed</span>
										</>
									)}
								</span>
							)}

							{/* Original Estimate */}
							{task.original_estimate && task.original_estimate !== '0' && task.integration_item_type?.toLowerCase() === 'task' && (
								<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-semibold">
									<span>OE: {task.original_estimate}</span>
									{task.original_estimate_computed && (
										<CustomTooltip title="Original estimate computed by Cadence Pro">
											<span className="text-amber-500 cursor-help">★</span>
										</CustomTooltip>
									)}
									{task.completed_original_estimate !== undefined && task.completed_original_estimate !== null && (
										<>
											<span className="text-blue-400 dark:text-blue-500">|</span>
											<span className="text-green-600 dark:text-green-400">{task.completed_original_estimate}</span>
											{task.completed_original_estimate_computed && (
												<CustomTooltip title="Completed estimate computed by Cadence Pro">
													<span className="text-amber-500 cursor-help">★</span>
												</CustomTooltip>
											)}
											<span className="text-blue-600 dark:text-blue-300 font-normal">Completed</span>
										</>
									)}
								</span>
							)}


							{/* Closed Outside Sprint Indicator */}
							{isSprintFilter && task.closed_outside_sprint && (
								<CustomTooltip title="This work item was closed outside the sprint period">
									<span className="inline-flex items-center gap-1 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 px-2 py-0.5 rounded-md text-[10px] font-medium">
										<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											<path strokeLinecap="round" strokeLinejoin="round" d="M9 14l2 2 4-4" />
										</svg>
										Closed Outside
									</span>
								</CustomTooltip>
							)}
						</div>

						{/* Bottom Row: Duration & Dates | Avatar + Progress */}
						<div className="mt-3 flex items-center justify-between">
							{/* Left: Duration + Dates */}
							<div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
								<span className="flex items-center gap-1">
									<FiClock className="w-3.5 h-3.5" />
									{task.duration}d
								</span>
								<span className="flex items-center gap-1">
									<FiCalendar className="w-3.5 h-3.5" />
									{task.due_date
										? `Due ${formatDate(task.due_date)}`
										: `${formatDate(task.start)} - ${formatDate(task.end)}`
									}
								</span>
							</div>
							{/* Right: Avatar + Progress */}
							<div className="flex items-center gap-3">
								{/* Assigned Avatar */}
								{task.responsible_id && (() => {
									const resourceInfo = getResourceInfo(task.responsible_id);
									const name = resourceInfo?.name || '';
									const imageUrl = resourceInfo?.image_url;
									const initials = name ? (name.split(' ').length > 1
										? (name.split(' ')[0][0] + name.split(' ')[name.split(' ').length - 1][0]).toUpperCase()
										: name.slice(0, 2).toUpperCase()) : '??';
									return (
										<CustomTooltip title={`Assigned to: ${name || task.responsible_id}`}>
											<div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden hover:scale-110 transition-transform cursor-pointer">
												{imageUrl ? (
													<img src={imageUrl} alt={name} className="w-full h-full object-cover" />
												) : (
													<span className="text-[9px] font-semibold text-gray-600 dark:text-gray-300 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 w-full h-full flex items-center justify-center">
														{initials}
													</span>
												)}
											</div>
										</CustomTooltip>
									);
								})()}
								{/* Progress Bar */}
								{task.completion !== undefined && (
									<div className="flex items-center gap-2">
										<div className="w-16 h-1.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
											<div
												className="h-full rounded-full transition-all duration-500 ease-out"
												style={{
													width: `${task.completion}%`,
													backgroundColor: statusColor
												}}
											/>
										</div>
										<span className="text-xs font-semibold text-gray-600 dark:text-slate-300 min-w-[32px] text-right">
											{task.completion}%
										</span>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</TaskHoverTooltip >
		);
	};

	return (
		<Portal>
			<Drawer isOpen={isOpen} onClose={onClose} position="right">
				<div className="h-screen w-full flex flex-col bg-white dark:bg-slate-900 overflow-hidden">
					{/* Header */}
					<div className="flex-shrink-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700/50 px-6 py-5">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center gap-3">
								<button
									type="button"
									className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200"
									onClick={onClose}
								>
									<HiArrowLeft className="w-5 h-5" />
								</button>
								<div>
									<h2 className="text-lg font-bold text-gray-900 dark:text-white">
										{resourceName
											? `Work Items for ${resourceName}`
											: filterTitle || 'All Work Items'}
									</h2>
									<p className="text-xs text-gray-500 dark:text-slate-400">
										{sortedTasks.length} {sortedTasks.length === 1 ? 'work item' : 'work items'} found
									</p>
								</div>
							</div>
							<button
								type="button"
								className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-all duration-200"
								onClick={onClose}
							>
								<HiX className="w-5 h-5" />
							</button>
						</div>

						{/* Filters Row */}
						<div className="flex items-center gap-2 flex-wrap">
							{/* Status Filter */}
							{!hideStatusFilter && (
								<StatusFilterWithColors
									statusFilters={statusFilters}
									setStatusFilters={setStatusFilters}
									isOpen={statusDropdownOpen}
									setIsOpen={setStatusDropdownOpen}
									statusCounts={statusCounts}
								/>
							)}

							<SortDropdown
								value={sortBy}
								onChange={(val) => setSortBy(val)}
								showStackRank={isSingleTeamSelected || isSprintFilter}
							/>



							{/* Search - inline styled */}
							<div className="relative flex-1 min-w-[180px]">
								<HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<input
									type="text"
									placeholder="Search work items..."
									value={localSearchText}
									onChange={(e) => setLocalSearchText(e.target.value)}
									className="w-full pl-9 pr-3 py-2 bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-300/30 text-gray-900 dark:text-white placeholder-gray-400 transition-colors h-[38px]"
								/>
							</div>
						</div>
					</div>

					{/* Task List */}
					<div className="flex-1 overflow-y-auto px-4 py-4 bg-white dark:bg-slate-900">
						{/* Grouped by indicator with filter dropdown */}
						{groupByTeam && groupedTasks && (
							<div className="flex items-center justify-between gap-2 mb-4 px-3 py-2 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700">
								<div className="flex items-center gap-2">
									<svg className="w-4 h-4 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
										<path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
									</svg>
									<span className="text-sm font-medium text-gray-700 dark:text-slate-300">Grouped by Team</span>
								</div>
								{/* Filter dropdown */}
								<div className="relative">
									<button
										onClick={() => setGroupFilterOpen(!groupFilterOpen)}
										className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-md text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
									>
										<span>{selectedGroupFilter === 'all' ? `All Teams (${groupedTasks.sortedGroupNames.length})` : selectedGroupFilter}</span>
										<svg className={`w-3.5 h-3.5 transition-transform ${groupFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
										</svg>
									</button>
									{groupFilterOpen && (
										<div className="absolute right-0 top-full mt-1 z-50 w-64 max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
											<button
												onClick={() => { setSelectedGroupFilter('all'); setGroupFilterOpen(false); }}
												className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 ${selectedGroupFilter === 'all' ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-gray-300'}`}
											>
												All teams ({groupedTasks.sortedGroupNames.length})
											</button>
											{groupedTasks.sortedGroupNames.map((groupKey: string) => (
												<button
													key={groupKey}
													onClick={() => { setSelectedGroupFilter(groupKey); setGroupFilterOpen(false); }}
													className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 ${selectedGroupFilter === groupKey ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-gray-300'}`}
												>
													{groupKey} ({groupedTasks.groups[groupKey].length})
												</button>
											))}
										</div>
									)}
								</div>
							</div>
						)}
						<div className="space-y-3">
							{sortedTasks.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-16 text-center">
									<div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-4">
										<HiSearch className="w-8 h-8 text-gray-400 dark:text-slate-500" />
									</div>
									<h3 className="text-lg font-semibold text-gray-700 dark:text-slate-300 mb-1">
										No work items found
									</h3>
									<p className="text-sm text-gray-500 dark:text-slate-400 max-w-xs">
										{localSearchText
											? 'Try adjusting your search or filters'
											: 'No work items match the current filters'
										}
									</p>
								</div>
							) : groupByTeam && groupedTasks ? (
								// Render grouped by team and project (filtered by selectedGroupFilter)
								(selectedGroupFilter === 'all' ? groupedTasks.sortedGroupNames : groupedTasks.sortedGroupNames.filter((k: string) => k === selectedGroupFilter)).map((groupKey: string) => (
									<div key={groupKey} className="mb-6">
										{/* Group Header */}
										<div className="flex items-center gap-2 mb-3 px-2">
											<div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1.5 rounded-lg">
												<svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
													<path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
												</svg>
												<span className="font-semibold text-sm text-indigo-700 dark:text-indigo-300">{groupKey}</span>
											</div>
											<span className="text-xs text-gray-500 dark:text-gray-400">({groupedTasks.groups[groupKey].length} work items)</span>
											<div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
										</div>
										{/* Group's Tasks */}
										<div className="space-y-3">
											{groupedTasks.groups[groupKey].map((task: Task, index: number) => renderTaskCard(task, index))}
										</div>
									</div>
								))
							) : (
								// Render flat list
								sortedTasks.map((task: Task, index: number) => renderTaskCard(task, index))
							)}
						</div>
					</div>
				</div>
			</Drawer>
		</Portal>
	);
};

export default AllTasksDrawer;
